/**
 * CRUD Service Sync Utility
 * 
 * Synchronizes collection configurations to crud-service-universal running on localhost:3030
 * 
 * Usage:
 * - syncCollectionToCrud(projectId, collectionName, collectionData)
 * - syncAllCollections(projectId, collections)
 */

const CRUD_SERVICE_URL = 'http://localhost:3030';

/**
 * Send a collection configuration to crud-service-universal
 * @param {string} projectId - Project identifier
 * @param {string} collectionName - Name of the collection
 * @param {object} collectionData - Collection configuration object
 * @returns {Promise<boolean>} Success status
 */
export async function syncCollectionToCrud(projectId, collectionName, collectionData) {
  try {
    // Transform to CRUD service collection definition format
    const crudCollectionDef = transformToCollectionDefinition(collectionData);
    
    // Use the new PUT endpoint for collection definitions
    const response = await fetch(`${CRUD_SERVICE_URL}/api/collections/definitions/${collectionName}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Project-Id': projectId
      },
      body: JSON.stringify(crudCollectionDef)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ Failed to sync collection ${collectionName}:`, response.status, errorText); /* eslint-disable-line no-console */
      return false;
    }

    const result = await response.json();
    console.log(`✅ Synced collection ${collectionName} to crud-service-universal:`, result); /* eslint-disable-line no-console */
    return true;
  } catch (error) {
    console.error(`❌ Error syncing collection ${collectionName} to crud-service-universal:`, error); /* eslint-disable-line no-console */
    
    // Check if service is available
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      console.warn('⚠️ crud-service-universal may not be running on localhost:3030'); /* eslint-disable-line no-console */
    }
    
    return false;
  }
}

/**
 * Transform collection data from frontend format to CRUD service collection definition format
 * @param {Object} collectionData - The frontend collection configuration
 * @returns {Object} CRUD service collection definition
 */
function transformToCollectionDefinition(collectionData) {
  const properties = {};
  const required = [];
  
  // Add user-defined fields
  if (collectionData.fields && Array.isArray(collectionData.fields)) {
    collectionData.fields.forEach(field => {
      const fieldType = mapFieldType(field.type);
      properties[field.name] = {
        type: fieldType,
        description: field.description || ''
      };
      
      if (field.required) {
        required.push(field.name);
      }
      
      // Add additional constraints
      if (fieldType === 'string' && field.maxLength) {
        properties[field.name].maxLength = field.maxLength;
      }
      if (fieldType === 'string' && field.minLength) {
        properties[field.name].minLength = field.minLength;
      }
      if (fieldType === 'number' && field.minimum !== undefined) {
        properties[field.name].minimum = field.minimum;
      }
      if (fieldType === 'number' && field.maximum !== undefined) {
        properties[field.name].maximum = field.maximum;
      }
      if (field.enum && Array.isArray(field.enum)) {
        properties[field.name].enum = field.enum;
      }
      if (field.format) {
        properties[field.name].format = field.format;
      }
    });
  }
  
  // Add mandatory CRUD fields
  properties._id = {
    type: 'string',
    pattern: '^[a-fA-F0-9]{24}$',
    __mia_configuration: { type: 'ObjectId' },
    description: 'Hexadecimal identifier of the document in the collection'
  };
  properties.creatorId = {
    type: 'string',
    description: 'User id that has created this object'
  };
  properties.createdAt = {
    type: 'string',
    format: 'date-time',
    description: 'Date of the request that has performed the object creation'
  };
  properties.updaterId = {
    type: 'string',
    description: 'User id that has requested the last change successfully'
  };
  properties.updatedAt = {
    type: 'string',
    format: 'date-time',
    description: 'Date of the request that has performed the last change'
  };
  properties.__STATE__ = {
    type: 'string',
    enum: ['PUBLIC', 'DRAFT', 'TRASH', 'DELETED'],
    description: 'The state of the document'
  };
  
  // Build indexes (filter out _id indexes - MongoDB manages these automatically)
  const indexes = [];
  if (collectionData.indexes && Array.isArray(collectionData.indexes)) {
    collectionData.indexes.forEach(index => {
      // Skip indexes on _id field - MongoDB creates this automatically
      if (index.name === '_id' || 
          (index.fields && index.fields.length === 1 && index.fields[0].name === '_id')) {
        console.log(`⚠️ Skipping _id index - MongoDB manages this automatically`); /* eslint-disable-line no-console */
        return;
      }
      
      const indexDef = {
        name: index.name,
        type: index.type || 'normal',
        unique: index.unique || false,
        fields: (index.fields || []).map(f => ({
          name: f.name,
          order: f.order || 1
        }))
      };
      
      if (index.weights) {
        indexDef.weights = index.weights;
      }
      
      indexes.push(indexDef);
    });
  }
  
  return {
    name: collectionData.name,
    endpointBasePath: collectionData.endpointBasePath || `/${collectionData.name}`,
    defaultState: collectionData.defaultState || 'DRAFT',
    schema: {
      type: 'object',
      properties,
      required,
      additionalProperties: false
    },
    indexes,
    tags: collectionData.tags || []
  };
}

/**
 * Map frontend field type to JSON Schema type
 * @param {string} fieldType - Frontend field type
 * @returns {string} JSON Schema type
 */
function mapFieldType(fieldType) {
  const typeMap = {
    'string': 'string',
    'number': 'number',
    'boolean': 'boolean',
    'Date': 'string',
    'ObjectId': 'string',
    'Array_string': 'array',
    'Array_number': 'array',
    'Array_RawObject': 'array',
    'RawObject': 'object'
  };
  return typeMap[fieldType] || 'string';
}

/**
 * Sync all collections for a project to crud-service-universal
 * @param {string} projectId - Project identifier
 * @param {object} collections - Collections object {collectionName: collectionData}
 * @returns {Promise<{success: number, failed: number}>} Sync results
 */
export async function syncAllCollections(projectId, collections) {
  if (!projectId || !collections) {
    return {success: 0, failed: 0};
  }

  const results = {
    success: 0,
    failed: 0
  };

  const collectionEntries = Object.entries(collections);
  
  if (collectionEntries.length === 0) {
    console.log('ℹ️ No collections to sync'); /* eslint-disable-line no-console */
    return results;
  }

  console.log(`🔄 Syncing ${collectionEntries.length} collections to crud-service-universal...`); /* eslint-disable-line no-console */

  // Sync collections sequentially to avoid overwhelming the service
  for (const [name, data] of collectionEntries) {
    // Skip deleted collections
    if (data.deleted) {
      continue;
    }

    const success = await syncCollectionToCrud(projectId, name, data);
    if (success) {
      results.success++;
    } else {
      results.failed++;
    }
  }

  console.log(`📊 Sync complete: ${results.success} succeeded, ${results.failed} failed`); /* eslint-disable-line no-console */
  return results;
}

/**
 * Check if crud-service-universal is available
 * @returns {Promise<boolean>} Service availability status
 */
export async function checkCrudServiceAvailability() {
  try {
    const response = await fetch(`${CRUD_SERVICE_URL}/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(2000) // 2 second timeout
    });
    
    return response.ok;
  } catch (error) {
    return false;
  }
}

const crudServiceSync = {
  syncCollectionToCrud,
  syncAllCollections,
  checkCrudServiceAvailability,
  CRUD_SERVICE_URL
};

export default crudServiceSync;
